<script setup lang="ts">
import { ref, watch } from 'vue';
import {
  UiButton,
  UiDialog,
  UiDivider,
  UiIconButton,
  UiInputField,
  UiText,
} from '@shopexperts/shared';
import PageUrlCombobox from './PageUrlCombobox.vue';
import {
  createEmptyLink,
  createEmptyLinkGroup,
  parseLinkGroupsValue,
  serializeLinkGroupsValue,
  type LinkGroup,
} from '../../utils/linkGroups';

const props = defineProps<{
  /** Current stored JSON string. */
  value: string;
}>();

const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
  (event: 'apply', value: string): void;
}>();

const groups = ref<LinkGroup[]>(seed());

function seed(): LinkGroup[] {
  const parsed = parseLinkGroupsValue(props.value);
  return parsed.length ? parsed : [createEmptyLinkGroup()];
}

// Re-seed when the dialog is reopened without being remounted.
watch(open, (isOpen) => {
  if (isOpen) groups.value = seed();
});

function addGroup() {
  groups.value = [...groups.value, createEmptyLinkGroup()];
}

function removeGroup(index: number) {
  groups.value = groups.value.filter((_, i) => i !== index);
}

function moveGroup(index: number, direction: -1 | 1) {
  const target = index + direction;
  if (target < 0 || target >= groups.value.length) return;
  const next = [...groups.value];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  groups.value = next;
}

function addLink(group: LinkGroup) {
  group.links.push(createEmptyLink());
}

function removeLink(group: LinkGroup, index: number) {
  group.links.splice(index, 1);
}

function apply() {
  emit('apply', serializeLinkGroupsValue(groups.value));
  open.value = false;
}
</script>

<template>
  <UiDialog
    v-model="open"
    title="Link groups"
    text="Each group renders as a heading with a row of link chips. Search a page by name to fill its URL, or type any URL directly."
    size="xl"
    primary-action-label="Apply"
    secondary-action-label="Cancel"
    hide-scrollbar
    @primary-action="apply"
    @secondary-action="open = false"
  >
    <div class="flex flex-col gap-4">
      <div
        v-for="(group, groupIndex) in groups"
        :key="groupIndex"
        class="flex flex-col gap-3 rounded-lg border border-se-gray-200 p-3"
      >
        <div class="flex items-center justify-between gap-2">
          <UiText as="p" variant="label" :text="`Group ${groupIndex + 1}`" />
          <div class="flex shrink-0 items-center gap-1">
            <UiIconButton
              icon="chevron-down"
              aria-label="Move group up"
              variant="ghost"
              hover-style="highlight"
              class="h-7 w-7 rotate-180"
              :disabled="groupIndex === 0"
              @click="moveGroup(groupIndex, -1)"
            />
            <UiIconButton
              icon="chevron-down"
              aria-label="Move group down"
              variant="ghost"
              hover-style="highlight"
              class="h-7 w-7"
              :disabled="groupIndex === groups.length - 1"
              @click="moveGroup(groupIndex, 1)"
            />
            <UiIconButton
              icon="delete"
              aria-label="Delete group"
              variant="ghost"
              hover-style="highlight"
              class="h-7 w-7 text-se-danger"
              @click="removeGroup(groupIndex)"
            />
          </div>
        </div>

        <UiInputField
          v-model="group.heading"
          :name="`link-group-${groupIndex}-heading`"
          placeholder="Group heading, e.g. Shopify experts by UK city"
        />

        <UiDivider />

        <div
          v-for="(link, linkIndex) in group.links"
          :key="linkIndex"
          class="flex items-start gap-2"
        >
          <!-- Width classes on wrapper divs: UiInputField forwards class to an
               inner node that keeps w-full, so sizing the components directly
               lets the label swallow the row and collapse the combobox. -->
          <div class="w-2/5 min-w-0 shrink-0">
            <UiInputField
              v-model="link.label"
              :name="`link-group-${groupIndex}-link-${linkIndex}-label`"
              placeholder="Label"
            />
          </div>
          <div class="min-w-0 flex-1">
            <PageUrlCombobox
              v-model="link.href"
              :name="`link-group-${groupIndex}-link-${linkIndex}-href`"
              @select-page-label="link.label = $event"
            />
          </div>
          <UiIconButton
            icon="delete"
            aria-label="Delete link"
            variant="ghost"
            hover-style="highlight"
            class="mt-1 h-7 w-7 shrink-0 text-se-danger"
            @click="removeLink(group, linkIndex)"
          />
        </div>

        <UiButton
          label="Add link"
          icon="plus"
          variant="outline"
          size="sm"
          class="self-start"
          @click="addLink(group)"
        />
      </div>

      <UiButton
        label="Add group"
        icon="plus"
        variant="outline"
        size="sm"
        class="self-start"
        @click="addGroup"
      />
    </div>
  </UiDialog>
</template>
